// src/lib/intake/incomeCardMapper.js
//
// Ticket 064 (Advisor Intake I5). The ONLY card-to-fact converter in the browser.
// IncomeCard edits Calculator-shaped items (createEmptyIncomeItem in
// src/hooks/incomeCardConfig.js); the intake stores income FACTS. This module
// converts both ways. It is a port of the server's calculatorSessionToIncome
// (supabase/functions/_shared/facts/adapters/incomeCalculator.ts) and
// incomeToCalculatorSlice, plus the intake rules below. A Deno test
// (incomeCardParity.test.ts) runs the same cases through both and compares them.
// If the card item shape changes, change this file and that test with it.
//
// INTAKE RULES (John, ticket 064):
//   D3  A card with any field touched but a blank or 0 amount is kept and saved
//       as a confirmed zero amount (known_zero). A card with nothing touched is
//       dropped (no fact). "Touched" = the item differs from a fresh default card
//       of the same type, ignoring only the id (isCardItemTouched).
//   N1  On a touched card whose source country is known and not Spain, a blank or
//       0 "foreign tax paid" is saved as known_zero. A Spanish-source card does
//       not show the field, so it stays unanswered. tax_withheld_spain has no
//       field on the card and stays unanswered.
//   Facts vs conclusions (CLAUDE.md rule 7): only what the person typed or chose.
//   Nothing is guessed: an unset choice is left out (the server fills it in as
//   "unanswered"). The browser never sends `source`.
//
// Output items carry only the fact keys that are SET. Keys left out are
// "unanswered" (or "not applicable") to the server, which fills them in.

import { INCOME_CARD_CONFIG, createEmptyIncomeItem } from '../../hooks/incomeCardConfig'
import {
  INCOME_CURRENCY_OPTIONS,
  INCOME_PERIOD_OPTIONS,
  INCOME_SOURCE_COUNTRY_OPTIONS,
  INCOME_AMOUNT_BASIS_OPTIONS,
  INCOME_SUBTYPE_OPTIONS,
  EXPENSE_PATH_OPTIONS,
  DEPRECIATION_SOURCE_OPTIONS,
  ACTIVITY_CLASSIFICATION_OPTIONS,
  AUTONOMO_TYPE_OPTIONS,
  EMPLOYMENT_WORK_LOCATION_OPTIONS,
  RENTAL_EXPENSE_KEYS,
  BUSINESS_EXPENSE_KEYS,
} from '../sharedOptions'

const MAX_ID_LENGTH = 64
const MAX_LABEL_LENGTH = 200

export const known = (value) => ({ status: 'known_value', value })
export const zero = () => ({ status: 'known_zero' })

const has = (list, v) => typeof v === 'string' && list.some((o) => o.value === v)
const isNum = (v) => typeof v === 'number' && Number.isFinite(v)

export function cardConfigFor(incomeType) {
  return INCOME_CARD_CONFIG.find((c) => c.income_type === incomeType) || null
}

// ── Touched detection (D3) ───────────────────────────────────────────────────

// Stable deep equality for plain JSON-like values.
function same(a, b) {
  if (a === b) return true
  if (typeof a !== typeof b || a === null || b === null || typeof a !== 'object') return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  const ka = Object.keys(a)
  const kb = Object.keys(b)
  if (ka.length !== kb.length) return false
  return ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && same(a[k], b[k]))
}

// The scratch field is absent on a fresh card; 0 / empty counts as absent too.
function comparable(item) {
  const { id, ...rest } = item
  if (!rest._fast_path_net_entered) delete rest._fast_path_net_entered
  return rest
}

// True when the person changed ANYTHING on the card. A person who types something
// and restores the default is untouched. Changing only the label, period or
// currency IS touched.
export function isCardItemTouched(item, cardConfig) {
  const fresh = createEmptyIncomeItem(cardConfig, item.owner || 'self')
  return !same(comparable(item), comparable(fresh))
}

export function isTouched(item) {
  const cfg = cardConfigFor(item.income_type)
  return cfg ? isCardItemTouched(item, cfg) : false
}

// ── Rental subtype <-> jurisdiction + type (same table as incomeFacts.ts) ─────

function rentalFactsFromSubtype(subtype) {
  if (subtype === 'spanish_property_long_term') return { jurisdiction: 'spain', rentalType: 'long_term_residential' }
  if (subtype === 'spanish_property_short_term') return { jurisdiction: 'spain', rentalType: 'short_term_tourist' }
  if (subtype === 'foreign_property') return { jurisdiction: 'foreign', rentalType: null }
  return { jurisdiction: null, rentalType: null }
}

function rentalSubtypeFromFacts(jurisdiction, rentalType) {
  if (jurisdiction === 'foreign') return 'foreign_property'
  if (jurisdiction === 'spain') {
    if (rentalType === 'long_term_residential') return 'spanish_property_long_term'
    if (rentalType === 'short_term_tourist') return 'spanish_property_short_term'
  }
  return null
}

function subtypesFor(incomeType) {
  return INCOME_SUBTYPE_OPTIONS.filter((o) => o.income_type === incomeType).map((o) => o.value)
}

function defaultAmountBasis(incomeType) {
  return incomeType === 'other' ? 'net_entered' : 'gross_entered'
}

// ── Card item -> fact item ───────────────────────────────────────────────────

// 0 -> known_zero, above 0 -> known_value, anything else -> null (left out).
function numberFact(v) {
  if (!isNum(v) || v < 0) return null
  return v === 0 ? zero() : known(v)
}

function positiveFact(v) {
  return isNum(v) && v > 0 ? known(v) : null
}

function mapFact(map, keys) {
  if (typeof map !== 'object' || map === null || Array.isArray(map)) return null
  const out = {}
  for (const k of keys) {
    const n = map[k.value]
    out[k.value] = isNum(n) && n >= 0 ? n : 0
  }
  return known(out)
}

function setIf(target, key, fact) {
  if (fact) target[key] = fact
}

function cardItemToFact(raw, workLocation) {
  const type = raw.income_type
  const id = String(raw.id).slice(0, MAX_ID_LENGTH)
  const rental = type === 'rental'
  const selfEmp = type === 'self_employment'
  const out = {
    id,
    owner: raw.owner === 'spouse' ? 'spouse' : 'self',
    income_type: type,
    label: typeof raw.label === 'string' ? raw.label.slice(0, MAX_LABEL_LENGTH) : '',
    currency: 'EUR',
    period: 'annual',
  }

  let amountUnrepresentable = false
  if (has(INCOME_CURRENCY_OPTIONS, raw.currency)) out.currency = raw.currency
  else amountUnrepresentable = true
  if (has(INCOME_PERIOD_OPTIONS, raw.amount_period)) out.period = raw.amount_period
  else amountUnrepresentable = true
  if (raw.input_precision === 'estimated') out.amount_precision = 'estimated'

  if (typeof raw.source_country === 'string' && raw.source_country.length > 0) {
    out.source_country = has(INCOME_SOURCE_COUNTRY_OPTIONS, raw.source_country)
      ? known(raw.source_country)
      : known('OTHER')
  }

  if (!rental && subtypesFor(type).length > 0 && typeof raw.income_subtype === 'string' && subtypesFor(type).includes(raw.income_subtype)) {
    out.income_subtype = known(raw.income_subtype)
  }

  let amount = null
  if (rental) {
    const legacy = rentalFactsFromSubtype(raw.income_subtype)
    if (legacy.jurisdiction) out.rental_jurisdiction = known(legacy.jurisdiction)
    if (legacy.rentalType) out.rental_type = known(legacy.rentalType)
    const path = has(EXPENSE_PATH_OPTIONS, raw.rental_expense_path) ? raw.rental_expense_path : null
    const dep = isNum(raw.rental_depreciation_amount) ? raw.rental_depreciation_amount : 0
    if (path) {
      out.expense_path = known(path)
      if (path === 'fast_path_net') {
        const scratch = raw._fast_path_net_entered
        const entered = isNum(scratch) ? scratch : isNum(raw.net_amount_original) ? raw.net_amount_original + dep : null
        amount = numberFact(entered)
      } else {
        amount = numberFact(raw.gross_amount_original)
        setIf(out, 'expenses', mapFact(raw.rental_expenses, RENTAL_EXPENSE_KEYS))
      }
    }
    if (has(DEPRECIATION_SOURCE_OPTIONS, raw.rental_depreciation_source)) {
      out.depreciation_source = known(raw.rental_depreciation_source)
    }
    if (isNum(raw.rental_depreciation_amount) && raw.rental_depreciation_amount > 0) {
      out.depreciation_amount = known(raw.rental_depreciation_amount)
    } else if (raw.rental_depreciation_amount === 0 && out.depreciation_source) {
      out.depreciation_amount = zero()
    }
    setIf(out, 'purchase_price', numberFact(raw.rental_purchase_price))
  } else if (selfEmp) {
    const path = has(EXPENSE_PATH_OPTIONS, raw.business_expense_path) ? raw.business_expense_path : null
    if (path) {
      out.expense_path = known(path)
      if (path === 'fast_path_net') {
        const scratch = raw._fast_path_net_entered
        amount = numberFact(isNum(scratch) ? scratch : raw.net_amount_original)
      } else {
        amount = numberFact(raw.gross_amount_original)
        setIf(out, 'expenses', mapFact(raw.business_expenses, BUSINESS_EXPENSE_KEYS))
      }
    }
    if (has(ACTIVITY_CLASSIFICATION_OPTIONS, raw.activity_classification)) out.activity_classification = known(raw.activity_classification)
    if (typeof raw.activity_started_this_year === 'boolean') out.activity_started_this_year = known(raw.activity_started_this_year)
    const ratio = raw.withholding_ratio
    if (isNum(ratio) && ratio > 0 && ratio <= 1) out.withholding_ratio = known(ratio)
    else if (ratio === 0) out.withholding_ratio = zero()
  } else {
    amount = numberFact(raw.gross_amount_original)
    if (has(INCOME_AMOUNT_BASIS_OPTIONS, raw.amount_basis)
      && (type === 'other' || raw.amount_basis !== defaultAmountBasis(type))) {
      out.amount_basis = known(raw.amount_basis)
    }
  }
  // An unrepresentable currency or period: we cannot interpret the figure.
  if (amountUnrepresentable) amount = null
  setIf(out, 'amount', amount)

  // Foreign tax paid. N1: a touched non-Spanish-source card with a blank or 0
  // field is a confirmed zero. The server's own importer does the same.
  const country = out.source_country
  const foreignTaxApplies = !!country && country.value !== 'ES'
  if (foreignTaxApplies) setIf(out, 'foreign_tax_paid', numberFact(raw.tax_paid_foreign))
  else setIf(out, 'foreign_tax_paid', positiveFact(raw.tax_paid_foreign))
  setIf(out, 'tax_withheld_spain', positiveFact(raw.tax_withheld_spain))

  // Intake-only (D8): where an employment card's work was done.
  if (type === 'employment' && workLocation && has(EMPLOYMENT_WORK_LOCATION_OPTIONS, workLocation[id])) {
    out.work_location = known(workLocation[id])
  }
  return out
}

// items: card-shaped items (both owners). workLocation: { [item id]: value }.
// Untouched cards are dropped (D3); an item whose type has no card is dropped.
export function cardItemsToIncomeFacts(items, { workLocation = {} } = {}) {
  const out = []
  const seen = new Set()
  for (const raw of items || []) {
    if (!raw || typeof raw !== 'object') continue
    const cfg = cardConfigFor(raw.income_type)
    if (!cfg || !isCardItemTouched(raw, cfg)) continue
    const fact = cardItemToFact(raw, workLocation)
    if (seen.has(fact.id)) continue
    seen.add(fact.id)
    out.push(fact)
  }
  return out
}

// ── Fact item -> card item ───────────────────────────────────────────────────

const factValue = (f) => (f && f.status === 'known_value' ? f.value : null)
function amountOf(f) {
  if (!f) return null
  if (f.status === 'known_zero') return 0
  if (f.status === 'known_value') return f.value
  return null
}

function mapToCard(fact, keys, base) {
  const v = fact && fact.status === 'known_value' ? fact.value : {}
  const out = { ...base }
  for (const k of keys) out[k.value] = Number(v[k.value]) || 0
  return out
}

const sum = (m) => Object.values(m || {}).reduce((s, v) => s + (Number(v) || 0), 0)

function factItemToCard(f) {
  const cfg = cardConfigFor(f.income_type)
  if (!cfg) return null
  const owner = f.owner === 'spouse' ? 'spouse' : 'self'
  const it = createEmptyIncomeItem(cfg, owner)
  it.id = f.id
  it.label = f.label
  it.amount_period = f.period
  it.currency = f.currency
  if (f.amount_precision === 'estimated') it.input_precision = 'estimated'

  const country = factValue(f.source_country)
  it.source_country = country
  it.source_country_status = country ? 'provided' : 'unknown'

  const taxFt = amountOf(f.foreign_tax_paid)
  it.tax_paid_foreign = taxFt == null ? 0 : taxFt
  const taxSp = amountOf(f.tax_withheld_spain)
  it.tax_withheld_spain = taxSp == null ? 0 : taxSp

  const A = amountOf(f.amount) ?? 0

  if (f.income_type === 'rental') {
    it.income_subtype = rentalSubtypeFromFacts(factValue(f.rental_jurisdiction), factValue(f.rental_type))
    const path = factValue(f.expense_path)
    it.rental_expense_path = path
    it.rental_depreciation_source = factValue(f.depreciation_source)
    it.rental_depreciation_amount = amountOf(f.depreciation_amount) ?? 0
    it.rental_purchase_price = amountOf(f.purchase_price)
    const dep = it.rental_depreciation_amount
    if (path === 'fast_path_net') {
      it._fast_path_net_entered = A
      it.amount_basis = dep > 0 ? 'gross_minus_expenses' : 'net_entered'
      it.deductible_expenses_original = dep
      it.net_amount_original = Math.max(0, A - dep)
      it.expense_detail_complete = true
    } else if (path === 'itemized') {
      it.rental_expenses = mapToCard(f.expenses, RENTAL_EXPENSE_KEYS, it.rental_expenses)
      it.gross_amount_original = A
      it.amount_basis = 'gross_minus_expenses'
      it.deductible_expenses_original = sum(it.rental_expenses) + dep
      it.net_amount_original = Math.max(0, A - sum(it.rental_expenses) - dep)
      it.expense_detail_complete = true
    }
  } else if (f.income_type === 'self_employment') {
    const path = factValue(f.expense_path)
    it.business_expense_path = path
    it.activity_classification = factValue(f.activity_classification)
    const started = factValue(f.activity_started_this_year)
    it.activity_started_this_year = typeof started === 'boolean' ? started : null
    const ratio = amountOf(f.withholding_ratio)
    it.withholding_ratio = ratio == null ? null : ratio
    if (path === 'fast_path_net') {
      it._fast_path_net_entered = A
      it.amount_basis = 'net_entered'
      it.gross_amount_original = A
      it.deductible_expenses_original = 0
      it.net_amount_original = A
      it.expense_detail_complete = true
    } else if (path === 'itemized') {
      it.business_expenses = mapToCard(f.expenses, BUSINESS_EXPENSE_KEYS, it.business_expenses)
      it.gross_amount_original = A
      it.amount_basis = 'gross_minus_expenses'
      it.deductible_expenses_original = sum(it.business_expenses)
      it.net_amount_original = Math.max(0, A - sum(it.business_expenses))
      it.expense_detail_complete = true
    }
  } else {
    it.income_subtype = factValue(f.income_subtype)
    it.gross_amount_original = A
    it.net_amount_original = A
    it.amount_basis = factValue(f.amount_basis) || it.amount_basis
  }
  return it
}

// income: the stored income domain (full facts). Returns { items, workLocation }.
export function incomeFactsToCardItems(income) {
  const items = []
  const workLocation = {}
  for (const f of (income && income.items) || []) {
    const card = factItemToCard(f)
    if (!card) continue
    items.push(card)
    const wl = factValue(f.work_location)
    if (f.income_type === 'employment' && wl) workLocation[f.id] = wl
  }
  return { items, workLocation }
}

// ── Autónomo block ───────────────────────────────────────────────────────────

// Starts with nulls, NOT createEmptyAutonomoFacts() (which pre-fills 'individual'
// and would store a guess, rule 7).
export function emptyAutonomoCard() {
  return {
    autonomo_type: null,
    registered_reta_last_2_years: null,
    previously_used_tarifa_plana: null,
    current_contribution_base: null,
    selected_contribution_base: null,
  }
}

// Only answered keys are returned; contribution bases are never asked here.
export function autonomoCardToFacts(a) {
  if (!a) return null
  const out = {}
  if (has(AUTONOMO_TYPE_OPTIONS, a.autonomo_type)) out.autonomo_type = known(a.autonomo_type)
  if (typeof a.registered_reta_last_2_years === 'boolean') {
    out.registered_reta_last_2_years = known(a.registered_reta_last_2_years)
    if (a.registered_reta_last_2_years === false && typeof a.previously_used_tarifa_plana === 'boolean') {
      out.previously_used_tarifa_plana = known(a.previously_used_tarifa_plana)
    }
  }
  return Object.keys(out).length > 0 ? out : null
}

export function autonomoFactsToCard(autonomo) {
  const card = emptyAutonomoCard()
  if (!autonomo) return card
  const type = factValue(autonomo.autonomo_type)
  if (type) card.autonomo_type = type
  const reta = factValue(autonomo.registered_reta_last_2_years)
  if (typeof reta === 'boolean') card.registered_reta_last_2_years = reta
  const prev = factValue(autonomo.previously_used_tarifa_plana)
  if (typeof prev === 'boolean') card.previously_used_tarifa_plana = prev
  return card
}

export function isAutonomoComplete(a) {
  if (!a || !has(AUTONOMO_TYPE_OPTIONS, a.autonomo_type)) return false
  if (typeof a.registered_reta_last_2_years !== 'boolean') return false
  if (a.registered_reta_last_2_years === false && typeof a.previously_used_tarifa_plana !== 'boolean') return false
  return true
}
